// React
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Material UI
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox as MUICheckbox,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// custom project button with theme font sizes
import Button from '@components/atoms/Button/Button';
// Redux
import { connect } from 'react-redux';
// Services and Actions
import {
  fetchProgramBeneficiaries,
  saveBeneficiaryIndicators as saveBeneficiaryIndicatorsService,
} from '@services/programServices';
import { clearProgramBeneficiaries as clearProgramBeneficiariesAction } from '@actions/programActions';
import { fetchBeneficiaryIndicatorPeriod } from '@services/selectServices';
import { fetchIndicator } from '@services/indicatorServices';
import { clearIndicatorAction } from '@actions/indicatorActions';
// For fetching existing beneficiary indicator measurements
import axiosInstance from '@utils/axios';
// Components
import Select from '@components/atoms/Select/Select';
// selectActions does not clear indicatorPeriods, we'll rely on fetch

/**
 * Dialog for bulk adding of program indicator measurements.
 */
const BulkIndicatorDialog = ({
  open,
  onClose,
  indicator, // minimal indicator from program list
  detailedIndicator, // full indicator details
  programId,
  fetchList,
  clearList,
  data,
  fetchIndicatorPeriods,
  indicatorPeriods = [],
  fetchIndicatorDetails,
  clearIndicatorDetails,
  saveBeneficiaryIndicators,
}) => {
  const { program_beneficiaries = [] } = data;
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [measurements, setMeasurements] = useState({});
  // baseline measurement flags per beneficiary and period
  const [baselines, setBaselines] = useState({});
  // metadata (who/when added or edited) per beneficiary and period
  const [metadata, setMetadata] = useState({});

  // fetch data when dialog opens
  useEffect(() => {
    if (open) {
      if (programId) fetchList(programId);
      if (indicator && indicator.id) {
        fetchIndicatorPeriods(indicator.id);
        fetchIndicatorDetails(indicator.id);
      }
    }
  }, [open, programId, indicator, fetchList, fetchIndicatorPeriods, fetchIndicatorDetails]);
  // reset local state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedPeriods([]);
      setMeasurements({});
      setBaselines({});
    }
  }, [open]);
  // cleanup global state on unmount
  useEffect(
    () => () => {
      clearList();
      clearIndicatorDetails();
    },
    [clearList, clearIndicatorDetails],
  );
  // fetch existing indicator measurements and baseline flags in bulk when dialog opens
  useEffect(() => {
    if (
      open &&
      indicator &&
      indicator.id != null &&
      programId &&
      program_beneficiaries.length > 0
    ) {
      axiosInstance
        .get(`/beneficjenci/wskazniki/program/${programId}/wskaznik/${indicator.id}`)
        .then((res) => {
          const respData = res.data; // array of { beneficiaryId, values }
          const newMeasurements = {};
          const newBaselines = {};
          // build measurements, baselines and metadata maps
          const newMetadata = {};
          respData.forEach((entry) => {
            const bId = entry.beneficiaryId;
            newMeasurements[bId] = {};
            newBaselines[bId] = {};
            newMetadata[bId] = {};
            const vals = entry.values;
            if (vals && typeof vals === 'object' && !Array.isArray(vals)) {
              Object.values(vals).forEach((item) => {
                const pid = item.time_period_id;
                newMeasurements[bId][pid] = item.beneficiary_indicator_value;
                newBaselines[bId][pid] = item.base_measurement;
                newMetadata[bId][pid] = {
                  who_added: item.who_added ?? null,
                  add_date: item.add_date ?? null,
                  who_edited: item.who_edited ?? null,
                  edit_date: item.edit_date ?? null,
                };
              });
            }
          });
          setMeasurements(newMeasurements);
          setBaselines(newBaselines);
          setMetadata(newMetadata);
        })
        .catch((err) => {
          // handle or log error
          console.error('Failed to fetch bulk indicator measurements', err);
        });
    }
  }, [open, indicator, program_beneficiaries, programId]);

  // indicator type (support camelCase or snake_case) and its details
  const type = detailedIndicator?.indicatorType ?? detailedIndicator?.indicator_type;
  const typeDetails = detailedIndicator?.typeDetails;

  // handler for TextField inputs (types 3 and 4)
  const handleChange = (beneficiaryId, periodId) => (e) => {
    const { value } = e.target;
    setMeasurements((prev) => {
      const row = prev[beneficiaryId] ? { ...prev[beneficiaryId] } : {};
      row[periodId] = value;
      return { ...prev, [beneficiaryId]: row };
    });
  };
  // handler for custom Select inputs (types 1 and 2)
  const handleSelectChange = (beneficiaryId, periodId) => (name, value) => {
    setMeasurements((prev) => {
      const row = prev[beneficiaryId] ? { ...prev[beneficiaryId] } : {};
      row[periodId] = value;
      return { ...prev, [beneficiaryId]: row };
    });
  };

  const handleSave = () => {
    // prepare payload: list beneficiaries with their values for selected periods
    const payload = program_beneficiaries.map((b) => {
      const bId = b.id;
      const vals = {};
      selectedPeriods.forEach((pid) => {
        const meta = metadata[bId]?.[pid] || {};
        vals[pid] = {
          time_period_id: pid,
          beneficiary_indicator_value: measurements[bId]?.[pid] ?? null,
          base_measurement: baselines[bId]?.[pid] || false,
          who_added: meta.who_added,
          add_date: meta.add_date,
          who_edited: meta.who_edited,
          edit_date: meta.edit_date,
        };
      });
      return { beneficiaryId: bId, cwpId: b.cwp_id, values: vals };
    });
    // send bulk save request
    saveBeneficiaryIndicators(programId, indicator.id, payload).then(() => onClose());
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Uzupełnij pomiary wskaźnika{indicator && `: ${indicator.indicator_name}`}
      </DialogTitle>
      <DialogContent dividers>
        {/* Period selection as independent checkboxes */}
        <Box mb={2}>
          {/* Increase label size to match dialog standards */}
          <Typography variant="h6" component="div">
            Wybierz okresy pomiaru
          </Typography>
          {indicatorPeriods.map((p) => (
            <FormControlLabel
              key={p.value}
              control={
                <MUICheckbox
                  checked={selectedPeriods.includes(p.value)}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setSelectedPeriods((prev) =>
                      checked ? [...prev, p.value] : prev.filter((x) => x !== p.value),
                    );
                  }}
                />
              }
              label={p.name}
            />
          ))}
        </Box>
        {/* Measurements table */}
        {selectedPeriods.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8 }}>Beneficjent</th>
                {selectedPeriods.map((pid) => {
                  const p = indicatorPeriods.find((x) => x.value === pid);
                  return (
                    <th key={pid} style={{ textAlign: 'left', padding: 8 }}>
                      {p?.name}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {program_beneficiaries.map((b) =>
                b.beneficiary_name !== 'anonimizacja' ? (
                  <tr key={b.id}>
                    <td style={{ padding: 8 }}>
                      {`${b.beneficiary_name || b.name} ${b.beneficiary_last_name || ''}`}
                    </td>
                    {selectedPeriods.map((pid) => (
                      <td key={pid} style={{ padding: 8 }}>
                        {/* baseline measurement flag */}
                        <FormControlLabel
                          control={
                            <MUICheckbox
                              checked={baselines[b.id]?.[pid] || false}
                              onChange={() => {
                                setBaselines((prev) => {
                                  const row = prev[b.id] ? { ...prev[b.id] } : {};
                                  row[pid] = !row[pid];
                                  return { ...prev, [b.id]: row };
                                });
                              }}
                            />
                          }
                          label="Pomiar bazowy"
                        />{' '}
                        {metadata[b.id]?.[pid] && (
                          <Tooltip
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: { sx: { fontSize: '1rem' } },
                            }}
                            title={
                              <>
                                {metadata[b.id][pid].who_added && (
                                  <div>
                                    Dodano: {metadata[b.id][pid].who_added} dnia{' '}
                                    {metadata[b.id][pid].add_date}
                                  </div>
                                )}
                                {metadata[b.id][pid].who_edited && (
                                  <div>
                                    Edytowano: {metadata[b.id][pid].who_edited} dnia{' '}
                                    {metadata[b.id][pid].edit_date}
                                  </div>
                                )}
                              </>
                            }
                          >
                            <InfoOutlinedIcon
                              fontSize="medium"
                              style={{ marginLeft: 4, verticalAlign: 'middle' }}
                            />
                          </Tooltip>
                        )}
                        {type === 'INDICATOR_YESNO' && (
                          <Select
                            variant="outlined"
                            label=""
                            data={[
                              { value: 'tak', name: 'tak' },
                              { value: 'nie', name: 'nie' },
                            ]}
                            text="value"
                            onChange={handleSelectChange(b.id, pid)}
                            selectValue={measurements[b.id]?.[pid] || ''}
                          />
                        )}
                        {type === 'INDICATOR_SELECT' && (
                          <Select
                            variant="outlined"
                            label=""
                            data={typeDetails?.select || []}
                            text="value"
                            onChange={handleSelectChange(b.id, pid)}
                            selectValue={measurements[b.id]?.[pid] || ''}
                          />
                        )}
                        {type === 'INDICATOR_TEXT' && (
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={measurements[b.id]?.[pid] || ''}
                            onChange={handleChange(b.id, pid)}
                          />
                        )}
                        {type === 'INDICATOR_NUMERICAL' && (
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="number"
                            inputProps={{
                              min: typeDetails?.numerical?.from,
                              max: typeDetails?.numerical?.to,
                            }}
                            value={measurements[b.id]?.[pid] || ''}
                            onChange={handleChange(b.id, pid)}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ) : null,
              )}
            </tbody>
          </table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={selectedPeriods.length === 0}
        >
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BulkIndicatorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // minimal indicator data from program list
  indicator: PropTypes.shape({ id: PropTypes.number, indicator_name: PropTypes.string }),
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchList: PropTypes.func.isRequired,
  clearList: PropTypes.func.isRequired,
  data: PropTypes.shape({
    program_beneficiaries: PropTypes.arrayOf(PropTypes.object),
  }),
  fetchIndicatorPeriods: PropTypes.func.isRequired,
  indicatorPeriods: PropTypes.arrayOf(PropTypes.object),
  // detailed indicator data
  detailedIndicator: PropTypes.object,
  fetchIndicatorDetails: PropTypes.func.isRequired,
  clearIndicatorDetails: PropTypes.func.isRequired,
  saveBeneficiaryIndicators: PropTypes.func,
};

BulkIndicatorDialog.defaultProps = {
  indicator: null,
  data: { program_beneficiaries: [] },
  indicatorPeriods: [],
  detailedIndicator: null,
  saveBeneficiaryIndicators: null,
};

const mapStateToProps = ({ programReducer, selectReducer, indicatorReducer }) => ({
  data: programReducer.beneficiaries,
  indicatorPeriods: selectReducer.indicatorPeriods || [],
  detailedIndicator: indicatorReducer.indicator,
});

const mapDispatchToProps = (dispatch) => ({
  fetchList: (id) => dispatch(fetchProgramBeneficiaries(id)),
  clearList: () => dispatch(clearProgramBeneficiariesAction()),
  fetchIndicatorPeriods: (id) => dispatch(fetchBeneficiaryIndicatorPeriod(id)),
  fetchIndicatorDetails: (id) => dispatch(fetchIndicator(id)),
  clearIndicatorDetails: () => dispatch(clearIndicatorAction()),
  // bulk save indicators for program beneficiaries
  saveBeneficiaryIndicators: (programId, indicatorId, payload) =>
    dispatch(saveBeneficiaryIndicatorsService(programId, indicatorId, payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkIndicatorDialog);
